import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UploadTemplateModel } from '@/Models/_Microservices/OnlineArchive/UploadTemplateModel';
import { asyncActions } from './asyncActions';
import { Nullable } from '@/Types';
import { ModalDataType } from '@/Types/ModalDataType';
import { arrayUtils } from '@/Utils';

export type UploadTemplatesDataState = {
  uploadTemplates: UploadTemplateModel[];
  selectedTemplate: Nullable<UploadTemplateModel>;
  uploadTemplateModal: ModalDataType<UploadTemplateModel>;
};

const initialState: UploadTemplatesDataState = {
  uploadTemplates: [],
  selectedTemplate: null,
  uploadTemplateModal: { visible: false },
};

const uploadTemplatesDataSlice = createSlice({
  name: 'uploadTemplatesData',
  initialState: initialState,
  reducers: {
    selectUploadTemplate(state, action: PayloadAction<Nullable<UploadTemplateModel>>) {
      state.selectedTemplate = state.uploadTemplates.find((item) => item.id == action.payload?.id) ?? null;
    },
    setUploadTemplateModal(state, action: PayloadAction<ModalDataType<UploadTemplateModel>>) {
      const { visible, data } = action.payload;

      state.uploadTemplateModal = {
        visible,
        data: state.uploadTemplates.find((item) => item.id == data?.id),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.getUploadTemplates.fulfilled, (state, action) => {
        state.uploadTemplates = arrayUtils.sortBy(action.payload, 'templateName');
      })
      .addCase(asyncActions.createUploadTemplate.fulfilled, (state, action) => {
        const { uploadTemplate } = action.payload;

        state.uploadTemplates.push(uploadTemplate);

        if (uploadTemplate.isForAutoSave) {
          const result = state.uploadTemplates.map((item) => {
            if (
              item.isForAutoSave &&
              item.id !== uploadTemplate.id &&
              item.documentTemplateGuid === uploadTemplate.documentTemplateGuid &&
              item.marketplaceAccountIds.filter((i) => uploadTemplate.marketplaceAccountIds.includes(i)).length !== 0
            ) {
              return { ...item, isForAutoSave: false };
            } // if

            return item;
          });

          state.uploadTemplates = arrayUtils.sortBy(result, 'templateName');
        } // if
      })
      .addCase(asyncActions.updateUploadTemplate.fulfilled, (state, action) => {
        const { uploadTemplate } = action.payload;

        if (state.selectedTemplate?.id == uploadTemplate.id) {
          state.selectedTemplate = uploadTemplate;
        }

        state.uploadTemplates = state.uploadTemplates.map((item) => {
          if (item.id === uploadTemplate.id) {
            return uploadTemplate;
          } // if

          if (
            uploadTemplate.isForAutoSave &&
            item.isForAutoSave &&
            item.documentTemplateGuid === uploadTemplate.documentTemplateGuid &&
            item.marketplaceAccountIds.filter((i) => uploadTemplate.marketplaceAccountIds.includes(i)).length !== 0
          ) {
            return { ...item, isForAutoSave: false };
          } // if

          return item;
        });
      })
      .addCase(asyncActions.deleteUploadTemplate.fulfilled, (state, action) => {
        state.uploadTemplates = state.uploadTemplates.filter((item) => item.id !== action.payload);
      });
  },
});

export const uploadTemplatesDataReducer = uploadTemplatesDataSlice.reducer;
export const uploadTemplatesDataActions = uploadTemplatesDataSlice.actions;
export const uploadTemplatesDataAsyncActions = asyncActions;
